import { z } from 'zod';
/**
 * Auction mechanics contract (Phase 9 — Bidding v2).
 *
 * Our upstream sources (insurance tenders) are BLIND auctions, so the
 * platform's own auction layer is an INTERNAL contest for the right to have
 * CarsFinders place the bid upstream. The per-car rules are described by
 * `auctionType`:
 *  - `blind_mirror` — today's mode: the car mirrors an insurance tender;
 *    users bid independently, no soft close, no live increments upstream.
 *  - `open_proxy`   — eBay/BaT-style open auction with proxy (max) bids,
 *    increment table and soft-close extensions.
 */
/** One row of the eBay-style increment table: applies while currentBid < upTo (null = open-ended last row). */
export const MinIncrementRowSchema = z.object({
    upTo: z.number().nullable(),
    increment: z.number(),
});
/**
 * Default increment table (CHF, eBay-style brackets). A car may override it
 * via `auction_config.minIncrementTable`.
 */
export const DEFAULT_MIN_INCREMENT_TABLE = [
    { upTo: 100, increment: 5 },
    { upTo: 500, increment: 10 },
    { upTo: 1_000, increment: 25 },
    { upTo: 5_000, increment: 50 },
    { upTo: 10_000, increment: 100 },
    { upTo: 50_000, increment: 250 },
    { upTo: null, increment: 500 },
];
/**
 * Minimum increment required on top of `currentBid`: the first row whose
 * `upTo` bound is still above the current bid (null = open-ended). An empty
 * table degrades to 1 so callers can never compute a zero increment.
 */
export function minIncrementFor(currentBid, table) {
    let lastIncrement = 1; // degenerate (empty) table must never yield 0
    for (const row of table) {
        if (row.upTo === null || currentBid < row.upTo)
            return row.increment;
        lastIncrement = row.increment;
    }
    return lastIncrement;
}
export const AuctionTypeSchema = z.enum(['blind_mirror', 'open_proxy']);
export const AuctionConfigSchema = z.object({
    /** blind_mirror = today's mode (mirror of the insurance tender). */
    auctionType: AuctionTypeSchema.default('blind_mirror'),
    /** eBay-style increment table. */
    minIncrementTable: z.array(MinIncrementRowSchema).default(DEFAULT_MIN_INCREMENT_TABLE),
    /** BaT 2-minute rule: a bid inside this window extends the auction (open_proxy only). */
    softCloseWindowSec: z.number().default(120),
    softCloseExtensionSec: z.number().default(120),
    maxAutoExtensions: z.number().default(30),
});
/**
 * Resolve a car's raw `auction_config` field (absent / partial / legacy junk)
 * into a fully-defaulted config. Unparseable input degrades to the
 * blind_mirror defaults — auction mechanics must never throw on read.
 */
export function resolveAuctionConfig(raw) {
    const parsed = AuctionConfigSchema.safeParse(raw ?? {});
    return parsed.success ? parsed.data : AuctionConfigSchema.parse({});
}
/**
 * Proxy (max) bid contract (`proxy_bids` collection, unique {userId, carId}).
 * The stored `maxAmount` is confidential — never expose another user's max.
 */
export const ProxyBidSchema = z
    .object({
    _id: z.unknown().nullish(),
    userId: z.string(),
    carId: z.string(), // String(car._id) — same convention as bids.carId
    maxAmount: z.number(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date().nullish(),
})
    .passthrough();
/**
 * Domain aggregates that emit outbox events ('relay' = bid-relay desk audit;
 * 'payment' = Phase 13 deposit-credited ledger events; 'deal' = Phase 13 deal
 * state-machine audit — both scaffold, gated inert by default; 'pricing' =
 * Phase 7 tariff publications, which are platform-scoped rather than
 * car-scoped and so belong to no existing aggregate).
 */
export const OutboxAggregateSchema = z.enum([
    'bid',
    'auction',
    'car',
    'relay',
    'payment',
    'deal',
    'kyc',
    'listing',
    'pricing',
    // Phase 9: account lifecycle rows (user.registered,
    // user.email_verification_requested, user.password_reset_requested) — the
    // notification events already exist in notification-events.ts; this is the
    // aggregate their producers file them under.
    'user',
]);
/**
 * Transactional outbox event (`outbox_events` collection).
 *
 * Written in the SAME MongoDB transaction as the domain change; the
 * OutboxDispatcherService publishes `payload.message` to `payload.channel`
 * and then stamps `publishedAt`. Delivery is at-least-once — consumers
 * deduplicate by `_id` (published messages carry it as `eventId`).
 */
export const OutboxEventSchema = z
    .object({
    _id: z.unknown().nullish(),
    aggregate: OutboxAggregateSchema,
    /** Domain event type, e.g. 'bid.inserted' | 'bid.updated' | 'bid.status_changed' | 'auction.extended'. */
    type: z.string(),
    payload: z.object({
        /** Redis fanout channel the message goes to (sse:bids / sse:admin:bids). */
        channel: z.string(),
        /** The exact wire message to publish (JSON-serialized as-is). */
        message: z.record(z.unknown()),
    }),
    createdAt: z.coerce.date(),
    publishedAt: z.coerce.date().nullable(),
})
    .passthrough();
//# sourceMappingURL=auction.js.map